// =============================================================================
// FILE: packages/renderer/src/pages/GovernmentFinancingPage.tsx
// PURPOSE: Public "R2 Government Deployment & Financing" page (overlay). Shows
//   the three deployment packages, discount programs, and cost transparency,
//   with a "Request Government Proposal" flow. No direct purchase.
// =============================================================================

import { useEffect, useState, type JSX } from 'react';
import { X, Landmark, HeartHandshake, CheckCircle2, Building2, Loader2, ShieldCheck } from 'lucide-react';
import { fetchPackages, requestGovProposal, money, type InstitutionalPackage, type DiscountProgram } from '../lib/institutional.js';

export function GovernmentFinancingPage({ onClose }: { onClose: () => void }): JSX.Element {
  const [packages, setPackages] = useState<InstitutionalPackage[]>([]);
  const [discounts, setDiscounts] = useState<DiscountProgram[]>([]);
  const [reqOpen, setReqOpen] = useState<{ pkg?: string } | null>(null);

  useEffect(() => { void fetchPackages().then((d) => { setPackages(d.packages); setDiscounts(d.discounts); }).catch(() => { /* */ }); }, []);

  return (
    <div data-testid="gov-financing-page" className="fixed inset-0 z-[10000] overflow-y-auto p-6" style={{ background: 'rgba(4,7,14,0.94)', backdropFilter: 'blur(8px)' }}>
      <div className="mx-auto max-w-6xl">
        <div className="flex items-start justify-between gap-4 mb-6">
          <div>
            <div className="text-[10px] uppercase tracking-[0.24em] text-amber-300/80 flex items-center gap-2"><Landmark className="h-3.5 w-3.5" /> Institutional Deployment</div>
            <h1 className="text-4xl font-bold tracking-tight text-zinc-50 mt-1">R2 Government Deployment &amp; Financing</h1>
            <p className="text-zinc-400 mt-2 text-[13px] max-w-2xl">Transparent deployment options for government agencies, municipalities, national infrastructure, faith organizations, universities & nonprofits. Understand the investment model — then request a tailored proposal.</p>
          </div>
          <button data-testid="gov-close" onClick={onClose} className="h-9 w-9 grid place-items-center rounded-md text-zinc-400 hover:text-zinc-100 shrink-0" style={{ border: '1px solid rgba(63,63,70,0.6)' }}><X className="h-4 w-4" /></button>
        </div>

        {/* Packages */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {packages.map((p) => (
            <div key={p.id} data-testid={`gov-pkg-${p.id}`} className="rounded-2xl p-5 flex flex-col" style={{ background: 'rgba(12,18,32,0.85)', border: `1px solid ${p.custom ? 'rgba(252,211,77,0.4)' : 'rgba(45,212,191,0.3)'}` }}>
              <div className="text-[13px] font-bold text-zinc-100">{p.name}</div>
              <div className="text-[11px] text-zinc-500 mt-1 mb-3">{p.purpose}</div>
              <div className="mb-3">
                {p.custom ? (
                  <div className="text-2xl font-extrabold text-amber-300">Custom Proposal</div>
                ) : (
                  <>
                    <div className="text-3xl font-extrabold text-zinc-50">{money(p.setup)}</div>
                    <div className="text-[11px] text-zinc-500">initial setup · {money(p.monthly)}/mo · {p.users} users</div>
                  </>
                )}
                {p.custom && <div className="text-[11px] text-amber-300/80 mt-0.5">{money(p.monthly)}/mo starting · {p.contact}</div>}
              </div>
              <ul className="space-y-1.5 flex-1">
                {p.features.map((f) => <li key={f} className="text-[12px] text-zinc-300 flex items-start gap-2"><CheckCircle2 className="h-3.5 w-3.5 mt-0.5 shrink-0 text-teal-400" /> {f}</li>)}
              </ul>
              <button data-testid={`gov-request-${p.id}`} onClick={() => setReqOpen({ pkg: p.id })}
                className="mt-4 w-full px-4 py-2.5 rounded-lg text-[12px] font-bold" style={{ background: p.custom ? '#fcd34d' : '#2dd4bf', color: '#04121b' }}>
                Request Government Proposal
              </button>
            </div>
          ))}
        </div>

        {/* Discount programs */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-5">
          {discounts.filter((d) => d.pct > 0).map((d) => (
            <div key={d.id} data-testid={`gov-discount-${d.id}`} className="rounded-xl p-5" style={{ background: 'rgba(52,211,153,0.06)', border: '1px solid rgba(52,211,153,0.3)' }}>
              <div className="flex items-center gap-2">
                {d.id === 'government' ? <ShieldCheck className="h-5 w-5 text-emerald-400" /> : <HeartHandshake className="h-5 w-5 text-emerald-400" />}
                <span className="text-[14px] font-bold text-zinc-100">{d.name}</span>
                <span className="ml-auto text-2xl font-extrabold text-emerald-400">{d.pct}% OFF</span>
              </div>
              <p className="text-[12px] text-zinc-400 mt-2">{d.desc}</p>
            </div>
          ))}
        </div>

        {/* Cost transparency */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-5">
          <div className="rounded-xl p-5" style={{ background: 'rgba(12,18,32,0.7)', border: '1px solid rgba(148,163,184,0.2)' }}>
            <div className="text-[11px] uppercase tracking-wide text-zinc-500 mb-2">Initial Investment covers</div>
            <ul className="space-y-1 text-[12px] text-zinc-300">{['Platform setup', 'Configuration', 'Training', 'Data preparation', 'Implementation'].map((x) => <li key={x} className="flex items-center gap-2"><span className="h-1 w-1 rounded-full bg-teal-400" /> {x}</li>)}</ul>
          </div>
          <div className="rounded-xl p-5" style={{ background: 'rgba(12,18,32,0.7)', border: '1px solid rgba(148,163,184,0.2)' }}>
            <div className="text-[11px] uppercase tracking-wide text-zinc-500 mb-2">Monthly Investment covers</div>
            <ul className="space-y-1 text-[12px] text-zinc-300">{['Software access', 'Cloud services', 'Support', 'Updates', 'AI services'].map((x) => <li key={x} className="flex items-center gap-2"><span className="h-1 w-1 rounded-full bg-teal-400" /> {x}</li>)}</ul>
          </div>
        </div>

        <div className="mt-6 mb-10 text-center">
          <button data-testid="gov-request-general" onClick={() => setReqOpen({})}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-lg text-[13px] font-bold" style={{ background: 'linear-gradient(135deg,#2dd4bf,#0d9488)', color: '#04121b' }}>
            <Building2 className="h-4 w-4" /> Request a Government Proposal
          </button>
          <p className="text-[11px] text-zinc-600 mt-2">No direct purchase — R2 prepares a tailored, founder-approved proposal for your organization.</p>
        </div>
      </div>

      {reqOpen && <RequestModal defaultPkg={reqOpen.pkg} onClose={() => setReqOpen(null)} />}
    </div>
  );
}

function RequestModal({ defaultPkg, onClose }: { defaultPkg?: string; onClose: () => void }): JSX.Element {
  const [org, setOrg] = useState('');
  const [orgType, setOrgType] = useState('Government / Municipality');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const submit = async () => {
    setError(null);
    if (!org.trim() || !name.trim() || !email.includes('@')) { setError('Organization, name and a valid email are required.'); return; }
    setBusy(true);
    try {
      await requestGovProposal({ organization: org.trim(), org_type: orgType, contact_name: name.trim(), email: email.trim(), package_interest: defaultPkg, message: message.trim() || undefined });
      setDone(true);
    } catch (e) { setError(e instanceof Error ? e.message : 'Could not submit.'); } finally { setBusy(false); }
  };

  return (
    <div data-testid="gov-request-modal" className="fixed inset-0 z-[10002] grid place-items-center p-6" style={{ background: 'rgba(4,7,14,0.9)', backdropFilter: 'blur(8px)' }}>
      <div className="relative w-full max-w-lg rounded-2xl overflow-hidden" style={{ background: 'rgba(9,13,22,0.98)', border: '1px solid rgba(45,212,191,0.35)' }}>
        <div className="flex items-center justify-between px-6 py-4" style={{ borderBottom: '1px solid rgba(45,212,191,0.2)' }}>
          <div className="text-sm font-bold text-zinc-100">Request Government Proposal</div>
          <button data-testid="gov-request-close" onClick={onClose} className="h-8 w-8 grid place-items-center rounded-md text-zinc-500 hover:text-zinc-200" style={{ border: '1px solid rgba(63,63,70,0.6)' }}><X className="h-4 w-4" /></button>
        </div>
        <div className="p-6 space-y-3">
          {done ? (
            <div data-testid="gov-request-success" className="text-center py-4">
              <CheckCircle2 className="h-10 w-10 text-emerald-400 mx-auto mb-2" />
              <div className="text-[15px] font-bold text-zinc-100">Request received</div>
              <p className="text-[12px] text-zinc-400 mt-1">The R2 team will prepare your deployment proposal and follow up shortly.</p>
              <button data-testid="gov-request-done" onClick={onClose} className="mt-4 px-5 py-2 rounded-lg text-[12px] font-bold bg-teal-400 text-zinc-900">Done</button>
            </div>
          ) : (
            <>
              <label className="text-[10px] uppercase tracking-wide text-zinc-500 block">Organization
                <input data-testid="gov-org" value={org} onChange={(e) => setOrg(e.target.value)} className="mt-1 w-full px-3 py-2 rounded-lg text-[13px] text-zinc-100 outline-none" style={{ background: 'rgba(2,6,12,0.6)', border: '1px solid rgba(148,163,184,0.25)' }} />
              </label>
              <label className="text-[10px] uppercase tracking-wide text-zinc-500 block">Organization Type
                <select data-testid="gov-org-type" value={orgType} onChange={(e) => setOrgType(e.target.value)} className="mt-1 w-full px-3 py-2 rounded-lg text-[13px] text-zinc-100 outline-none" style={{ background: 'rgba(2,6,12,0.6)', border: '1px solid rgba(148,163,184,0.25)' }}>
                  {['Government / Municipality', 'National Infrastructure', 'Catholic Church / Religious', 'University', 'Nonprofit / Community', 'Enterprise'].map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              </label>
              <div className="grid grid-cols-2 gap-3">
                <label className="text-[10px] uppercase tracking-wide text-zinc-500 block">Contact Name
                  <input data-testid="gov-name" value={name} onChange={(e) => setName(e.target.value)} className="mt-1 w-full px-3 py-2 rounded-lg text-[13px] text-zinc-100 outline-none" style={{ background: 'rgba(2,6,12,0.6)', border: '1px solid rgba(148,163,184,0.25)' }} />
                </label>
                <label className="text-[10px] uppercase tracking-wide text-zinc-500 block">Email
                  <input data-testid="gov-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1 w-full px-3 py-2 rounded-lg text-[13px] text-zinc-100 outline-none" style={{ background: 'rgba(2,6,12,0.6)', border: '1px solid rgba(148,163,184,0.25)' }} />
                </label>
              </div>
              <label className="text-[10px] uppercase tracking-wide text-zinc-500 block">Notes <span className="text-zinc-600">(optional)</span>
                <textarea data-testid="gov-message" value={message} onChange={(e) => setMessage(e.target.value)} rows={3} className="mt-1 w-full px-3 py-2 rounded-lg text-[13px] text-zinc-100 outline-none resize-none" style={{ background: 'rgba(2,6,12,0.6)', border: '1px solid rgba(148,163,184,0.25)' }} />
              </label>
              {error && <div data-testid="gov-request-error" className="text-[12px] text-red-400">{error}</div>}
              <button data-testid="gov-request-submit" onClick={submit} disabled={busy} className="w-full flex items-center justify-center gap-2 rounded-lg px-5 py-3 font-bold text-[13px] uppercase tracking-[0.12em] disabled:opacity-60" style={{ background: '#2dd4bf', color: '#04121b' }}>
                {busy ? <><Loader2 className="h-4 w-4 animate-spin" /> Submitting…</> : 'Submit Request'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
